import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal, MaskLine, Parallax } from "@/lib/anim";

const WORK = [
  {
    tag: "AI Platform",
    title: "Veluro AI",
    body: "An AI-powered growth platform that automates customer acquisition and marketing — your AI growth team.",
    img: "/work/veluro.png",
    url: "https://veluro.ai",
    accent: "text-teal",
  },
  {
    tag: "AI Product",
    title: "b→come",
    body: "Your AI life coach — personalized roadmaps and daily actions that help people become their best self.",
    img: "/work/bcome.png",
    url: "https://b-come.ai",
    accent: "text-cobalt",
  },
  {
    tag: "Web Platform",
    title: "Aspire Partners",
    body: "Turn a business network into residual income — a referral-partnership platform that monetizes introductions.",
    img: "/work/aspire.png",
    url: "https://partnerwithaspirepartners.com/",
    accent: "text-violet",
  },
  {
    tag: "Brand Site",
    title: "Newberry Wieters",
    body: "Charleston roots, statewide reach — a litigation boutique brand site for construction, insurance & complex disputes.",
    img: "/work/newberry.png",
    url: "https://newberrywieters.com/",
    accent: "text-magenta",
  },
  {
    tag: "Brand Site",
    title: "LuxeTini Escapes",
    body: "Escape the ordinary — a bespoke luxury travel brand crafting exceptional, personalized escapes.",
    img: "/work/luxetin.png",
    url: "https://luxetiniescapes.com/",
    accent: "text-teal",
  },
];

const WorkItem = ({ item, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const reverse = i % 2 === 1;

  return (
    <div
      ref={ref}
      className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${reverse ? "md:[direction:rtl]" : ""}`}
      data-testid={`work-item-${i}`}
    >
      <motion.a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/11] [direction:ltr] block"
        data-testid={`work-image-${i}`}
      >
        <motion.img
          style={{ y }}
          src={item.img}
          alt={item.title}
          className="w-full h-[120%] object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <span className="absolute top-4 left-4 text-[11px] uppercase tracking-[0.2em] text-white bg-black/50 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1.5">
          {item.tag}
        </span>
        <span className="absolute bottom-4 right-4 flex items-center gap-1.5 text-sm text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 bg-black/50 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
          Visit live site <ArrowUpRight size={15} weight="bold" />
        </span>
      </motion.a>

      <div className="[direction:ltr]">
        <Reveal>
          <span className={`text-xs uppercase tracking-[0.25em] ${item.accent}`}>
            {String(i + 1).padStart(2, "0")} / {item.tag}
          </span>
        </Reveal>
        <h3 className="mt-5 font-heading font-bold text-white text-3xl md:text-4xl leading-tight">
          <MaskLine>{item.title}</MaskLine>
        </h3>
        <Reveal delay={1}>
          <p className="mt-5 text-white/60 leading-relaxed max-w-md">{item.body}</p>
        </Reveal>
        <Reveal delay={2}>
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-white/80 border border-white/20 rounded-full px-5 py-2.5 hover:bg-white/10 hover:text-white transition-colors duration-300"
            data-testid={`work-link-${i}`}
          >
            {item.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            <ArrowUpRight size={16} weight="bold" />
          </a>
        </Reveal>
      </div>
    </div>
  );
};

export const Portfolio = () => {
  return (
    <section
      id="work"
      className="relative py-28 md:py-40 bg-black/20"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Parallax speed={0.12} className="max-w-2xl mb-20">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-teal/80">
              Selected work
            </span>
          </Reveal>
          <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-white text-3xl sm:text-4xl md:text-5xl lg:text-[3.75rem] leading-[1.02]">
            <MaskLine>Builders who think</MaskLine>
            <MaskLine delay={0.08}>like marketers.</MaskLine>
          </h2>
        </Parallax>

        <div className="flex flex-col gap-24 md:gap-32">
          {WORK.map((item, i) => (
            <WorkItem key={item.title} item={item} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
